/**
 * Text helpers shared by the scene solvers: greedy wrap with an injected
 * measure, the text entry the scene's text layer renders (SceneText: one
 * line start per wrapped line), cluster-item emission and the title block;
 * and which symbols a spec draws (its art, one icon per symbol).
 *
 * Pure.
 */

import { makeRng, seedFor, randRange } from '../lib/tapestryGenerative.js';
import { containerPath } from './hand.js';
import { templateArt } from './sceneTemplates.js';

/** Line height as a multiple of the font size (both archetypes). */
export const LINE_GAP = 1.12;

/** Saved specs from before the role repair used symbol_id for their icons. */
export function iconSymbolId(item) {
  return 'icon_symbol_id' in item ? item.icon_symbol_id : item.symbol_id;
}

/**
 * The art a spec draws, icons aside, as { symbolId, role }. Story boards
 * compose around their metaphor's template art (sceneTemplates.js); other
 * scenes name their own: the anchor is a backdrop, each element a mid.
 */
export function drawnArt(spec) {
  if (spec.archetype === 'story') return templateArt(spec.metaphor);
  const art = [];
  if (spec.anchor?.symbol_id) art.push({ symbolId: spec.anchor.symbol_id, role: 'backdrop' });
  for (const e of spec.elements || []) if (e.symbol_id) art.push({ symbolId: e.symbol_id, role: 'mid' });
  return art;
}

const ICON_LISTS = ['big_ideas', 'themes', 'clusters'];

/**
 * One symbol, drawn once: an icon whose symbol the scene already draws —
 * as its art, or as an earlier item's icon (big ideas, then themes, then
 * clusters, each in spec order) — is dropped, and its item lays out
 * without one. Spec order, so a newcomer never takes an older item's icon.
 */
export function oneIconPerSymbol(spec) {
  const drawn = new Set(drawnArt(spec).map((a) => a.symbolId));
  const out = { ...spec };
  for (const k of ICON_LISTS) {
    if (!spec[k]) continue;
    out[k] = spec[k].map((it) => {
      const symbolId = it && typeof it === 'object' ? iconSymbolId(it) : null;
      if (!symbolId) return it;
      if (!drawn.has(symbolId)) {
        drawn.add(symbolId);
        return it;
      }
      return { ...it, icon_symbol_id: null };
    });
  }
  return out;
}

const BASELINE = 0.8; // the baseline sits this far down the line box

export function fmt(n) {
  return Number(n.toFixed(1));
}

export function up(s) {
  return String(s || '').toUpperCase();
}

/**
 * Greedy wrap. Returns { lines, widths, w, h }; `h` uses `lineGap`.
 * Words are measured with a trailing space so the running width matches
 * how the line will be set; line widths are re-measured whole.
 */
export function wrap(text, fontSize, maxW, measure, lineGap = LINE_GAP) {
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = [];
  let curW = 0;
  for (const w of words) {
    const ww = measure(w + ' ', fontSize);
    if (cur.length && curW + ww > maxW) {
      lines.push(cur.join(' '));
      cur = [w];
      curW = ww;
    } else {
      cur.push(w);
      curW += ww;
    }
  }
  if (cur.length) lines.push(cur.join(' '));
  const widths = lines.map((l) => measure(l, fontSize));
  return { lines, widths, w: Math.max(0, ...widths), h: lines.length * fontSize * lineGap };
}

/** A single-line layout for text that is never wrapped. */
export function line(text, fontSize, measure) {
  return { lines: [text], widths: [measure(text, fontSize)] };
}

/**
 * A text sink: `pushText(id, lay, x, y, fontSize, family, color, opts)`
 * appends one text entry to `texts`: the wrapped `lines` and each line's
 * baseline start `{ x, y, tilt }` (`center` centres each line on `x`;
 * `lineHeight` overrides the register's), which components/SceneText
 * letters. `phrase` (the lines joined) is kept for the lab's debug views.
 */
export function textSink(texts) {
  // `group`: the block this text is drawn as part of (the reveal schedule
  // paces a block as one — set through `blockSink`, never by hand);
  // `verbatim`: the text is a sentence of the transcript as spoken (the
  // planner checked it), so a host may offer it as a link to that moment —
  // a paraphrase is not (TT-1174 note 5).
  return (id, lay, x, y, fontSize, family, color, { sent, group, verbatim = false, tilt = 0, uppercase = false, center = false, lineHeight = null } = {}) => {
    const lh = lineHeight || fontSize * LINE_GAP;
    const starts = lay.lines.map((ln, i) => {
      const lx = center ? x - lay.widths[i] / 2 : x;
      return { x: fmt(lx), y: fmt(y + lh * (i + BASELINE)), tilt };
    });
    texts.push({ id, lines: lay.lines, starts, phrase: lay.lines.join(' '), fontSize, fontFamily: family, color, uppercase, sent, group, verbatim });
  };
}

/**
 * A block's sinks: everything a solver draws as one thing — an idea with
 * its banner, claim and quote; a theme with its headline, boxes and
 * fragments — goes through the block it belongs to, and the reveal
 * schedule (scene/reveal.js) paces that block as one. An element cannot be
 * emitted without a block, so none can fall back to pacing by its own
 * sentence (TT-1174 note 4). `E` is the emission bucket ({ texts, shapes,
 * icons }); `group` the block id.
 */
export function blockSink(E, group) {
  const text = textSink(E.texts);
  return {
    text: (id, lay, x, y, fontSize, family, color, opts) => text(id, lay, x, y, fontSize, family, color, { ...opts, group }),
    shape: (shape) => E.shapes.push({ ...shape, group }),
    icon: (icon) => E.icons.push({ ...icon, group }),
  };
}

/** The sentence a cluster was last touched at (its own, a child's or a fragment's). */
export function latestSent(cl) {
  return Math.max(cl.sent ?? 0, ...(cl.children || []).flatMap((ch) => [ch.sent ?? 0, ...(ch.fragments || []).map((f) => f?.sent ?? 0)]));
}

/** A short stable hash of a phrase, for element ids that follow content. */
export function slug(text) {
  return seedFor(String(text || '')).toString(36);
}

const KIND_TAG = { icon: 'i', headline: 'h', box: 'b', subhead: 's', fragment: 'f', arrow: 'a', quote: 'q' };

/**
 * Paper under a line of text: a story's text brings its own paper where it
 * may sit on the art's wash (beat tags, quotes, theme lines — TT-1229), so an
 * outline, ray or rim never runs through its letters.
 */
export function paperPatch(B, id, rect, color, { opacity, sent, z = 1, rng = makeRng(seedFor(id, 9)) }) {
  B.shape({ id, kind: 'highlight', d: containerPath('box', rect, rng), stroke: 'none', fill: color, fillOpacity: opacity, strokeWidth: 0, sent, z });
}

const PAPER_PAD = 4;

/**
 * Emit a laid-out cluster (`local` from layoutCluster) at rect `r`:
 * spot icon, tilted headline, tint boxes with sub-heads, fragments hooked
 * by a short arrow, and script quotes (when the layout carried them).
 *
 * Ids follow content, not position (`<cluster>-<kind><hash of the text>`,
 * an icon's `<cluster>-i<symbol_id>`, TT-1150): an item added mid-cluster
 * gets its own id and every neighbour keeps its node — and its finished
 * intro — across the re-solve.
 *
 * `paper`: every line of text gets a paper patch (a story's themes may sit
 * on the art); the wall has no art under its text and passes none.
 */
export function emitClusterItems(cl, local, r, { E, palette, fonts, clusterPad, arrowStroke, arrowHead, paper = false }) {
  const B = blockSink(E, cl.id); // the whole tree reveals as one block
  const rng = makeRng(seedFor(cl.id, 17));
  const ox = r.x + clusterPad;
  const oy = r.y + clusterPad;
  const taken = new Set();
  const underText = (id, it, sent) => {
    if (paper) paperPatch(B, `${id}-pp`, { x: ox + it.x - PAPER_PAD, y: oy + it.y - PAPER_PAD / 2, w: it.w + PAPER_PAD * 2, h: it.h + PAPER_PAD }, palette.paper, { opacity: 0.85, sent });
  };
  for (const it of local.items) {
    let id = `${cl.id}-${KIND_TAG[it.kind] || 'x'}${it.kind === 'icon' ? it.symbolId : slug(it.key ?? it.text ?? '')}`;
    for (let k = 2; taken.has(id); k++) id = `${id}~${k}`;
    taken.add(id);
    const sent = it.sent ?? cl.sent;
    switch (it.kind) {
      case 'icon':
        B.icon({ id, symbolId: it.symbolId, x: ox + it.x, y: oy + it.y, w: it.w, h: it.h, color: palette.accent, sent: cl.sent });
        break;
      case 'headline':
        underText(id, it, cl.sent);
        B.text(id, it, ox + it.x, oy + it.y, it.fontSize, fonts.caps, palette.accent, { sent: cl.sent, uppercase: true, tilt: randRange(rng, -0.01, 0.01) });
        break;
      case 'box': {
        const j = () => randRange(rng, -1.5, 1.5);
        const { x, y, w, h } = { x: ox + it.x, y: oy + it.y, w: it.w, h: it.h };
        B.shape({
          id,
          kind: 'highlight',
          d: `M ${fmt(x + j())} ${fmt(y + j())} L ${fmt(x + w + j())} ${fmt(y + j())} L ${fmt(x + w + j())} ${fmt(y + h + j())} L ${fmt(x + j())} ${fmt(y + h + j())} Z`,
          stroke: 'none',
          fill: palette.tint,
          fillOpacity: 0.9,
          strokeWidth: 0,
          sent,
          z: 1,
        });
        break;
      }
      case 'subhead':
        underText(id, it, sent);
        B.text(id, it, ox + it.x, oy + it.y, it.fontSize, fonts.caps, palette.ink, { sent, uppercase: true });
        break;
      case 'fragment':
        underText(id, it, sent);
        B.text(id, it, ox + it.x, oy + it.y, it.fontSize, fonts.caps, palette.ink, { sent, uppercase: true, tilt: randRange(rng, -0.012, 0.012) });
        break;
      case 'arrow': {
        const x1 = ox + it.x;
        const y1 = oy + it.y;
        const x2 = ox + it.x2;
        const y2 = oy + it.y2;
        const hs = Math.min(arrowHead, (x2 - x1) * 0.6);
        // Cubic: first control at the corner (x1, y2), second on the head's line —
        // the curve leaves the box straight down and runs flat into the chevron
        // (the last stretch is a straight horizontal).
        B.shape({ id, kind: 'connector', d: `M ${fmt(x1)} ${fmt(y1)} C ${fmt(x1)} ${fmt(y2)} ${fmt(x1 + (x2 - x1) * 0.55)} ${fmt(y2)} ${fmt(x2)} ${fmt(y2)}`, stroke: palette.ink, fill: 'none', strokeWidth: arrowStroke, sent, z: 2 });
        B.shape({ id: `${id}-h`, kind: 'connector', d: `M ${fmt(x2 - hs)} ${fmt(y2 - hs * 0.6)} L ${fmt(x2)} ${fmt(y2)} L ${fmt(x2 - hs)} ${fmt(y2 + hs * 0.6)}`, stroke: palette.ink, fill: 'none', strokeWidth: arrowStroke, sent, z: 2 });
        break;
      }
      case 'quote':
        // A theme's quotes are the planner's verbatim sentences (story.py
        // keeps only those that are).
        underText(id, it, sent);
        B.text(id, it, ox + it.x, oy + it.y, it.fontSize, fonts.script, palette.ink, { sent, verbatim: true, tilt: randRange(rng, -0.008, 0.008) });
        break;
      default:
        break;
    }
  }
}

/**
 * Title block: an optional qualifier over the giant word (`word` falls back
 * to the spec id), scaled so the word never exceeds `maxFrac` of the sheet
 * width. Emits both texts and commits the reserved rect as `ctx.rects.title`.
 *
 * @param opts.sizes   { word, qualifier } font sizes
 * @param opts.center  centred on x (wall) or left-aligned at x (story)
 * @param opts.reserve { padL, padR, top, capFrac, below }: the reserved rect
 *                     spans the word ± padL/padR and top → ty + fs·capFrac + below
 * @returns { x, ty, titleFs, wordW } (ty = top of the word's line box)
 */
export function placeTitle(spec, ctx, { measure, E, commitSpot, fonts, palette, sizes, maxFrac, center, x, y, reserve }) {
  const { text } = blockSink(E, 'title');
  const t = spec.title || {};
  const word = up(t.word || spec.id);
  let titleFs = sizes.word;
  let wordW = measure(word, titleFs);
  const maxTitleW = ctx.sheet.w * maxFrac;
  if (wordW > maxTitleW) {
    titleFs *= maxTitleW / wordW;
    wordW = measure(word, titleFs);
  }
  let ty = y;
  if (t.qualifier) {
    const q = up(t.qualifier);
    text('title-q', line(q, sizes.qualifier, measure), x, ty, sizes.qualifier, fonts.caps, palette.ink, { center, uppercase: true });
    ty += sizes.qualifier * LINE_GAP;
  }
  text('title-w', { lines: [word], widths: [wordW] }, x, ty, titleFs, fonts.caps, palette.accent, { center, uppercase: true });
  const left = center ? x - wordW / 2 : x;
  const titleRect = { x: left - reserve.padL, y: reserve.top, w: wordW + reserve.padL + reserve.padR, h: ty + titleFs * reserve.capFrac + reserve.below - reserve.top };
  ctx.rects.title = titleRect;
  commitSpot(ctx, titleRect);
  return { x: left, ty, titleFs, wordW };
}
